'use client'

import { cx } from 'class-variance-authority'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

import { hyperEVM } from '@/config/chain/constants'
import { useBannerVisibility } from '@/domain/hooks/useBannerVisibility'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useWrongNetwork } from '@/domain/hooks/useWrongNetwork'
import { AnalyticsConsentBanner } from '@/features/analytics-consent/AnalyticsConsentBanner'
import { CheckOFAC } from '@/features/ofac/CheckOFAC'
import { SideDrawer } from '@/features/side-drawer/SideDrawer'
import { TopBar } from '@/features/top-nav/TopNav'
import { cn } from '@/ui/utils/style'
import { useAccount, useSwitchChain } from 'wagmi'
import { TOP_BANNER_ID, TopBanner } from '../../atoms/top-banner/TopBanner'

const InkeepFloatingButton = dynamic(
  () => import('@/ui/atoms/inkeep/InkeepFloatingButton').then((mod) => mod.InkeepFloatingButton),
  { ssr: false },
)

interface AppLayoutProps {
  children: React.ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const [mobileMenuCollapsed, setMobileMenuCollapsed] = useState(true)
  const { pageSupported } = usePageChainId()
  const { chainId } = useAccount()
  const isWrongNetwork = useWrongNetwork()
  const { switchChain } = useSwitchChain()

  const { handleCloseBanner, showBanner } = useBannerVisibility(TOP_BANNER_ID)

  useEffect(() => {
    if ((!pageSupported || isWrongNetwork) && chainId !== hyperEVM.id) {
      switchChain({ chainId: hyperEVM.id })
    }
  }, [pageSupported, isWrongNetwork, chainId, switchChain])

  return (
    <div className={cn('flex min-h-screen flex-col')}>
      <div className="fixed top-0 right-0 left-0 z-50 w-full">
        {showBanner && <TopBanner onClose={handleCloseBanner} />}
        <TopBar />
      </div>

      <div className={cn('flex flex-col xl:flex-row', showBanner && 'pt-16 md:pt-9')}>
        <main className={cx('isolate flex w-full grow flex-col xl:px-4', !mobileMenuCollapsed && 'hidden xl:flex')}>
          <CheckOFAC>{children}</CheckOFAC>
        </main>
      </div>

      <SideDrawer setMobileMenuCollapsed={setMobileMenuCollapsed} />
      <InkeepFloatingButton />
      <AnalyticsConsentBanner />
    </div>
  )
}
